import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Upload, X } from "lucide-react";

const cls = "w-full bg-[color:var(--ms-warm-white)] border border-[color:var(--ms-border)] focus:border-[color:var(--ms-charcoal)] px-3 py-2.5 text-sm outline-none text-[color:var(--ms-charcoal)]";

const blank = { title: "", slug: "", excerpt: "", content: "", cover_image: "", author: "Mini Style", tags: [], is_published: true };

export default function AdminBlog() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const fileRef = useRef(null);

    const load = () => api.get("/admin/blog").then(r => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            if (editing.id) await api.put(`/admin/blog/${editing.id}`, editing);
            else await api.post("/admin/blog", editing);
            toast.success("Salvato");
            setEditing(null);
            load();
        } catch (e) { toast.error("Salvataggio fallito"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminare articolo?")) return;
        await api.delete(`/admin/blog/${id}`);
        toast.success("Eliminato");
        load();
    };

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setEditing(prev => ({ ...prev, cover_image: r.data.url }));
        toast.success("Caricata");
    };

    return (
        <div data-testid="admin-blog">
            <div className="flex justify-between items-center mb-10">
                <div>
                    <div className="overline mb-2">Editoriale</div>
                    <h1 className="font-display text-4xl text-[color:var(--ms-charcoal)]">Blog</h1>
                </div>
                <button onClick={() => setEditing({ ...blank })} className="bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] px-5 py-3 text-xs tracking-[0.2em] uppercase flex items-center gap-2 hover:bg-[color:var(--ms-sage-dark)]" data-testid="blog-new"><Plus size={14} /> Nuovo articolo</button>
            </div>

            {items.length === 0 ? (
                <div className="panel p-16 text-center text-[color:var(--ms-muted)]">Nessun articolo ancora.</div>
            ) : (
                <div className="panel divide-y divide-[color:var(--ms-border)]">
                    {items.map(p => (
                        <div key={p.id} className="px-4 py-3 flex justify-between items-center text-sm" data-testid={`blog-row-${p.id}`}>
                            <div className="flex-1">
                                <div className="text-[color:var(--ms-charcoal)]">{p.title}</div>
                                <div className="text-xs text-[color:var(--ms-muted)]">/{p.slug} · {p.is_published ? "Pubblicato" : "Bozza"} · {new Date(p.created_at).toLocaleDateString("it-IT")}</div>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => setEditing(p)} className="border border-[color:var(--ms-border)] hover:border-[color:var(--ms-charcoal)] px-3 py-1.5 text-xs">MODIFICA</button>
                                <button onClick={() => remove(p.id)} className="hover:text-[color:var(--ms-terracotta)] p-1.5"><Trash2 size={14} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4">
                    <div className="bg-[color:var(--ms-warm-white)] border border-[color:var(--ms-border)] max-w-3xl w-full my-8 p-8 space-y-5" data-testid="blog-editor">
                        <div className="flex justify-between items-start">
                            <h2 className="font-display text-3xl text-[color:var(--ms-charcoal)]">{editing.id ? "Modifica articolo" : "Nuovo articolo"}</h2>
                            <button onClick={() => setEditing(null)} className="text-[color:var(--ms-muted)] hover:text-[color:var(--ms-charcoal)]"><X size={20} /></button>
                        </div>
                        <div><label className="overline block mb-2">Titolo</label><input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} className={cls} data-testid="blog-title" /></div>
                        <div><label className="overline block mb-2">Slug (automatico se vuoto)</label><input value={editing.slug || ""} onChange={e => setEditing({ ...editing, slug: e.target.value })} className={cls} /></div>
                        <div><label className="overline block mb-2">Estratto</label><textarea rows={2} value={editing.excerpt} onChange={e => setEditing({ ...editing, excerpt: e.target.value })} className={cls} /></div>
                        <div><label className="overline block mb-2">Immagine di copertina</label>
                            <div className="flex gap-2">
                                <input value={editing.cover_image} onChange={e => setEditing({ ...editing, cover_image: e.target.value })} className={cls} />
                                <button onClick={() => fileRef.current?.click()} className="border border-[color:var(--ms-border)] px-3 hover:border-[color:var(--ms-charcoal)]"><Upload size={14} /></button>
                                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
                            </div>
                        </div>
                        <div><label className="overline block mb-2">Contenuto</label><textarea rows={10} value={editing.content} onChange={e => setEditing({ ...editing, content: e.target.value })} className={cls} data-testid="blog-content" /></div>
                        <div><label className="overline block mb-2">Autore</label><input value={editing.author} onChange={e => setEditing({ ...editing, author: e.target.value })} className={cls} /></div>
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_published} onChange={e => setEditing({ ...editing, is_published: e.target.checked })} className="accent-[color:var(--ms-sage-dark)]" /> Pubblicato</label>
                        <div className="flex gap-3 justify-end">
                            <button onClick={() => setEditing(null)} className="border border-[color:var(--ms-border)] hover:border-[color:var(--ms-charcoal)] px-4 py-2 text-xs tracking-[0.2em] uppercase">Annulla</button>
                            <button onClick={save} className="bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] px-5 py-2 text-xs tracking-[0.2em] uppercase hover:bg-[color:var(--ms-sage-dark)]" data-testid="blog-save">Salva</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
